<template>
  <v-content class="application page">
    <v-container>
      <v-toolbar flat class="transparent">
        <v-breadcrumbs
          class="pl-0"
          :items="[
            { exact: true, text: 'Admin', to: '/dashboard' },
            { exact: true, text: 'Applications', to: '/applications' },
            { exact: true, text: $route.params.id, to: `/applications/${$route.params.id}` },
          ]"
        >
          <v-icon slot="divider">mdi-chevron-right</v-icon>
        </v-breadcrumbs>

        <v-spacer />

        <can I="create" a="OpportunityApplication">
          <TransferApplication :application="application" />
        </can>
        <can I="read" a="OpportunityApplication">
          <v-btn small flat fab @click="refresh">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </can>
        <can I="regenerate" a="OpportunityApplication">
          <v-tooltip left>
            <v-btn small flat fab slot="activator" @click="regenerateDocument">
              <v-icon v-if="regeneratingDocument">mdi-loading</v-icon>
              <v-icon v-else>mdi-refresh</v-icon>
            </v-btn>
            <span>Regenerate Application PDF</span>
          </v-tooltip>
        </can>
        <CopyButton />
        <v-tooltip left>
          <v-btn small flat fab slot="activator" :href="htmlApplicationUrl" target="_blank">
            <v-icon color="primary">mdi-code-tags</v-icon>
          </v-btn>
          <span>Preview HTML (new pipeline)</span>
        </v-tooltip>
        <v-tooltip left>
          <v-btn small flat fab slot="activator" :href="pdfApplicationUrl" target="_blank" color="red">
            <v-icon>mdi-file-pdf</v-icon>
          </v-btn>
          <span>Preview PDF (new pipeline)</span>
        </v-tooltip>
      </v-toolbar>

      <can I="read" a="OpportunityApplication">
        <ShowApplication
          v-if="application"
          :application="application"
          @refresh="refresh"
          @destroy="destroy"
          @withdraw="withdraw"
        />

        <can I="edit" a="OpportunityApplication">
          <v-card dark class="mt-3" v-if="application">
            <v-card-title flat>
              <v-badge right>
                <v-icon left>mdi-city</v-icon> <span class="title">Change Locations</span>
              </v-badge>
            </v-card-title>
            <v-divider />
            <div class="py-1 px-2">
              <div
                v-for="location in application.opportunity.locations"
                :key="`location-${location.id}`"
              >
                <v-checkbox
                  v-model="locations"
                  :label="location.address"
                  :value="location.id"
                  color="primary"
                ></v-checkbox>
              </div>
            </div>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" light @click="updateLocations">Update</v-btn>
            </v-card-actions>
          </v-card>
        </can>

        <can I="list" a="Note">
          <v-card dark class="mt-3" v-if="application && application.activities">
            <ApplicationNotes :application="application" />
          </v-card>
        </can>

        <v-card dark class="mt-3" v-if="application && application.chat">
          <ApplicationChat :application="application" />
        </v-card>

        <v-card
          dark
          v-if="application && application.questionnaires && application.questionnaires[0]"
          class="mt-3"
        >
          <v-card-title>
            <div class="headline">Questionnaires</div>
            <v-spacer />
            <v-tooltip left v-if="application.sections.screeningQuestion">
              <v-btn
                flat
                fab
                small
                slot="activator"
                color="red"
                :href="application.sections.screeningQuestion"
              >
                <v-icon>mdi-file-pdf</v-icon>
              </v-btn>
              <span>Preview Screening Questions Section</span>
            </v-tooltip>
          </v-card-title>
          <v-divider />
          <ApplicationQuestionnaire :value="application.questionnaires" @updated="refresh" />
        </v-card>

        <can I="read" a="Eligibility">
          <v-card dark class="mt-3" v-if="application && application.eligibility">
            <Eligibility :value="application.eligibility" />
          </v-card>
        </can>
      </can>
    </v-container>
  </v-content>
</template>

<script>
// Components
import ShowApplication from '@/components/applications/Show'
import Eligibility from '@/components/applications/Eligibility'
import ApplicationChat from '@/components/applications/Chat'
import ApplicationNotes from '@/components/applications_tracker/applications/ApplicationNotes'
import ApplicationQuestionnaire from '@/components/applications_tracker/applications/ApplicationQuestionnaire'
import TransferApplication from '@/components/applications/TransferApplication'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  components: {
    ApplicationChat,
    ApplicationNotes,
    ApplicationQuestionnaire,
    Eligibility,
    ShowApplication,
    TransferApplication,
  },
  created() {
    this.refresh().then(() => {
      this.locations = this.application.locations.map(l => l.id)
    })
  },
  data() {
    return {
      editing: false,
      locations: [],
      regeneratingDocument: false,
    }
  },
  computed: {
    application() {
      return this.$store.getters['admin/applications/find'](parseInt(this.$route.params.id))
    },
    htmlApplicationUrl() {
      if (!this.application) return null
      const token = (this.$auth && this.$auth.getToken && encodeURIComponent(this.$auth.getToken())) || ''
      return `${this.$root.$api || ''}/admin/html_documents/application/${this.application.id}?auth_token=${token}`
    },
    pdfApplicationUrl() {
      if (!this.application) return null
      const token = (this.$auth && this.$auth.getToken && encodeURIComponent(this.$auth.getToken())) || ''
      return `${this.$root.$api || ''}/admin/html_documents/application/${this.application.id}.pdf?auth_token=${token}`
    },
  },
  methods: {
    refresh() {
      return this.$store.dispatch('admin/applications/fetch', this.$route.params.id)
    },
    async regenerateDocument() {
      this.regeneratingDocument = true
      try {
        await this.$store.dispatch('admin/applications/regenerateDocument', this.$route.params.id)
        this.$store.commit('snackbar', {
          text: 'Application document regeneration requested.',
          type: 'success',
        })
      } catch (error) {
        this.$store.commit('snackbar', {
          text: 'There was a problem requesting application document regeneration.',
          type: 'error',
        })
      }
      this.regeneratingDocument = false
    },
    withdraw(reason) {
      this.$store
        .dispatch('admin/applications/withdraw', { id: this.$route.params.id, reason })
        .then(response => {
          // Success
          this.$store.commit('snackbar', {
            text: 'Application withdrawn',
            type: 'success',
          })
          this.refresh()
        })
        .catch(error => {
          // Failure
          this.$store.commit('snackbar', {
            text: 'There was a problem withdrawing the application',
            type: 'error',
          })
        })
    },
    destroy() {
      this.$store.dispatch('admin/applications/destroy', this.$route.params.id).then(
        () => {
          // Success
          this.$store.commit('snackbar', {
            text: 'Application deleted',
            type: 'success',
          })
          this.$router.push('/applications')
        },
        () => {
          // Failure
          this.$store.commit('snackbar', {
            text: 'There was a problem deleting the application',
            type: 'error',
          })
        }
      )
    },
    updateLocations() {
      const locations = this.locations
      Vue.$http
        .patch(`admin/applications/${this.$route.params.id}/locations`, { locations })
        .then(
          () => {
            // Success
            this.$store.commit('snackbar', {
              text: 'Application locations updated',
              type: 'success',
            })
          },
          () => {
            // Failure
            this.$store.commit('snackbar', {
              text: 'There was a problem updating the application',
              type: 'error',
            })
          }
        )
        .then(this.refresh)
    },
  },
}
</script>
